import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI, usersAPI, rolesAPI, profileAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('user');

    if (token && storedUser) {
      authAPI.verify()
        .then(response => {
          if (response.success) {
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
          } else {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
          }
        })
        .catch(() => {
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const applySession = (response) => {
    if (response.success && response.data) {
      localStorage.setItem('auth_token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      // Prevent stale facility scope from a previous account/session.
      localStorage.removeItem('active_facility_id');
      setUser(response.data.user);
      return { success: true, user: response.data.user };
    }
    return { success: false, error: response.message || 'Login failed' };
  };

  const login = async (username, password) => {
    try {
      return applySession(await authAPI.login(username, password));
    } catch (error) {
      return { success: false, error: error.message || 'Invalid username or password' };
    }
  };

  const loginWithOtp = async (phone, otp) => {
    try {
      const response = await authAPI.otpVerify(phone, otp);
      // Verified phone with no account → unified signup (plan §12): no
      // session yet; the caller collects name + facility and registers.
      if (response.success && response.data?.isNewUser) {
        return { success: true, isNewUser: true, registrationToken: response.data.registrationToken };
      }
      return applySession(response);
    } catch (error) {
      return { success: false, error: error.message || 'Invalid or expired code' };
    }
  };

  const registerOwner = async (registrationToken, name, facilityName, password, confirmPassword) => {
    try {
      return applySession(await authAPI.registerOwner(registrationToken, name, facilityName, password, confirmPassword));
    } catch (error) {
      return { success: false, error: error.message || 'Could not complete signup' };
    }
  };

  const logout = () => {
    authAPI.logout().catch(() => {});
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user');
    localStorage.removeItem('active_facility_id');
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.verify();
      if (response.success) {
        setUser(response.data);
        localStorage.setItem('user', JSON.stringify(response.data));
        return response.data;
      }
    } catch (_) {
      /* ignore */
    }
    return null;
  };

  const hasPermission = useCallback((moduleId, accessType = 'read') => {
    if (!user) return false;
    if (user.role_name === 'Super Admin') return true;
    const perm = user.permissions?.[moduleId];
    if (!perm) return false;
    return accessType === 'write' ? perm.write : perm.read;
  }, [user]);

  const getAllUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      if (!response.success) return [];
      return (response.data || []).map(u => ({
        ...u,
        roleId: u.role_id,
        isActive: u.is_active,
        createdAt: u.created_at,
      }));
    } catch (error) {
      console.error('Error fetching users:', error);
      return [];
    }
  };

  const addUser = async (userData) => {
    const response = await usersAPI.create(userData);
    if (response.success) return response.data;
    throw new Error(response.message || 'Failed to create user');
  };

  const updateUser = async (userId, userData) => {
    const response = await usersAPI.update(userId, userData);
    if (response.success) {
      if (user && user.id === userId) {
        const updatedUser = { ...user, ...response.data };
        setUser(updatedUser);
        localStorage.setItem('user', JSON.stringify(updatedUser));
      }
      return response.data;
    }
    throw new Error(response.message || 'Failed to update user');
  };

  const deleteUser = async (userId) => {
    if (user && user.id === userId) {
      return { success: false, error: 'Cannot delete your own account' };
    }
    const response = await usersAPI.delete(userId);
    if (response.success) return { success: true };
    throw new Error(response.message || 'Failed to delete user');
  };

  const changePassword = async (currentPassword, newPassword, confirmPassword = newPassword) => {
    const response = await profileAPI.changePassword({
      current_password: currentPassword,
      new_password: newPassword,
      confirm_password: confirmPassword,
    });
    if (response.success) return { success: true };
    throw new Error(response.message || 'Failed to change password');
  };

  const getAllRoles = async () => {
    try {
      const response = await rolesAPI.getAll();
      return response.success ? (response.data || []) : [];
    } catch (error) {
      console.error('Error fetching roles:', error);
      return [];
    }
  };

  const getRoleById = async (roleId) => {
    try {
      const response = await rolesAPI.getById(roleId);
      return response.success ? response.data : null;
    } catch (error) {
      console.error('Error fetching role:', error);
      return null;
    }
  };

  const addRole = async (roleData) => {
    const response = await rolesAPI.create(roleData);
    if (response.success) return response.data;
    throw new Error(response.message || 'Failed to create role');
  };

  const updateRole = async (roleId, roleData) => {
    const response = await rolesAPI.update(roleId, roleData);
    if (response.success) return response.data;
    throw new Error(response.message || 'Failed to update role');
  };

  const deleteRole = async (roleId) => {
    const response = await rolesAPI.delete(roleId);
    if (response.success) return { success: true };
    throw new Error(response.message || 'Failed to delete role');
  };

  const isSuperAdmin = user?.role_name === 'Super Admin';

  const value = useMemo(() => ({
    user,
    loading,
    login,
    loginWithOtp,
    registerOwner,
    logout,
    refreshUser,
    hasPermission,
    getAllUsers,
    addUser,
    updateUser,
    deleteUser,
    changePassword,
    getAllRoles,
    getRoleById,
    addRole,
    updateRole,
    deleteRole,
    isAuthenticated: !!user,
    isSuperAdmin,
    permissions: user?.permissions || {},
  }), [user, loading, hasPermission, isSuperAdmin, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
